import React, {useState, useEffect} from "react";
import ReactModal from "react-modal";

import "./FacilitySelect.css";
import {BreadCrumbTrail} from "../../../../common/breadcrumb-trail/BreadcrumbTrail";
import facilityIcon from "../../../../../resources/images/facility-icon.png";
import {StandardButton} from "../../../../common/button/standard-button/StandardButton";
import {findLocationsBasedOnSortOrder} from "../../../../../services/FacilityService";

export const FacilitySelect = ({context, setContext, navigateTo, setSpinnerText, showToaster}) => {
    ReactModal.setAppElement('#root');

    const [showModal, setShowModal] = useState(false);
    const [locationClicked, setLocationClicked] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [locationList, setLocationList] = useState([]);

    useEffect(() => {
        setSpinnerText("Retrieving Locations");
        const fetchData = async () => {
            const [data, error] = await findLocationsBasedOnSortOrder(context.username);
            if (data) {
                if (data.responseStatus === "Success") {
                    const locations = mapLocations(data.locations);
                    sortLocations(locations);
                    setLocationList(locations);
                } else {
                    showToaster(data);
                }
            } else {
                showToaster({
                    responseStatus: "Error",
                    responseMessage: error.message,
                });
            }
            setSpinnerText(null);
        }
        fetchData();

        setContext((prevState) => ({
            ...prevState,
            viewMode: false,
            breadcrumbs: {
                ...prevState.breadcrumbs,
                facilityBreadcrumb: {
                    ...prevState.breadcrumbs.facilityBreadcrumb,
                    type: "Location",
                    screenKey: "facilitySelect"
                }
            },
            editMode: true,
        }));
    }, []);

    const mapLocations = (locations) => {
        const mappedLocations = [];

        locations.forEach((location) => {
            let mappedLocation = null;
            if (location.facility !== "") {
                mappedLocation = {
                    externalReference: location.externalReference,
                    index: location.index,
                    name: location.facility,
                    levelOfSupport: {value: 5, label: "Facility"},
                }
            } else if (location.subDistrict !== "") {
                mappedLocation = {
                    externalReference: location.externalReference,
                    index: location.index,
                    name: location.subDistrict,
                    levelOfSupport: {value: 4, label: "Sub District"},
                }
            } else if (location.district !== "") {
                mappedLocation = {
                    externalReference: location.externalReference,
                    index: location.index,
                    name: location.district,
                    levelOfSupport: {value: 3, label: "District"},
                }
            } else if (location.province !== "") {
                mappedLocation = {
                    externalReference: location.externalReference,
                    index: location.index,
                    name: location.province,
                    levelOfSupport: {value: 2, label: "Provincial"},
                }
            } else if (location.country !== "") {
                mappedLocation = {
                    externalReference: location.externalReference,
                    index: location.index,
                    name: location.country,
                    levelOfSupport: {value: 1, label: "National"},
                }
            }
            mappedLocations.push(mappedLocation);
        });

        return mappedLocations;
    }

    const sortLocations = (locations) => {
        locations.sort((firstLocation, secondLocation) => {
            return firstLocation.index - secondLocation.index;
        });
    }

    const handleListItemClick = (location) => {
        setLocationClicked(true);

        const {facilityScreen, topicAreaScreen, problemScreen, rootCauseScreen, changeIdeaScreen, PDSAScreen, breadcrumbs} = context;
        if (facilityScreen || topicAreaScreen || problemScreen || rootCauseScreen || changeIdeaScreen || PDSAScreen) {
            if (breadcrumbs.topicAreaBreadcrumb) {
                setShowModal(true);
                setSelectedLocation(location);
            } else {
                setContext((prevState) => ({
                    ...prevState,
                    viewMode: false,
                    breadcrumbs: [],
                    changeIdeaScreen: null,
                    problemScreen: null,
                    rootCauseScreen: null,
                    topicAreaScreen: null,
                    facilityScreen: {
                        ...prevState.facilityScreen,
                        levelOfSupport: location.levelOfSupport,
                        locationBreadCrumbText: location.name,
                        locationExternalReference: location.externalReference,
                    },
                }));
                navigateTo("topicAreaOne");
            }
        } else {
            setContext((prevState) => ({
                ...prevState,
                facilityScreen: {
                    ...prevState.facilityScreen,
                    levelOfSupport: location.levelOfSupport,
                    locationBreadCrumbText: location.name,
                    locationExternalReference: location.externalReference,
                },
            }));
            navigateTo("topicAreaOne");
        }
    }

    const handleChooseAnotherLocationClick = () => {
        setLocationClicked(false);

        const {facilityScreen, topicAreaScreen, problemScreen, rootCauseScreen, changeIdeaScreen, PDSAScreen, breadcrumbs} = context;
        if (facilityScreen || topicAreaScreen || problemScreen || rootCauseScreen || changeIdeaScreen || PDSAScreen) {
            if (breadcrumbs.topicAreaBreadcrumb) {
                setShowModal(true);
            } else {
                setContext((prevState) => ({
                    ...prevState,
                    viewMode: false,
                    breadcrumbs: [],
                    changeIdeaScreen: null,
                    problemScreen: null,
                    rootCauseScreen: null,
                    topicAreaScreen: null,
                    facilityScreen: null,
                }));
                navigateTo("facilitySearch");
            }
        } else {
            navigateTo("facilitySearch");
        }
    }

    const handleNewProblemClick = () => {
        if (locationClicked) {
            setContext((prevState) => ({
                ...prevState,
                viewMode: false,
                breadcrumbs: [],
                changeIdeaScreen: null,
                problemScreen: null,
                rootCauseScreen: null,
                topicAreaScreen: null,
                facilityScreen: {
                    ...prevState.facilityScreen,
                    levelOfSupport: selectedLocation.levelOfSupport,
                    locationBreadCrumbText: selectedLocation.name,
                    locationExternalReference: selectedLocation.externalReference,
                },
            }));
            navigateTo("topicAreaOne");
        } else {
            setContext((prevState) => ({
                ...prevState,
                viewMode: false,
                breadcrumbs: [],
                changeIdeaScreen: null,
                problemScreen: null,
                rootCauseScreen: null,
                topicAreaScreen: null,
                facilityScreen: null,
            }));
            navigateTo("facilitySearch");
        }
    }


    const locations = locationList.map((location, index) => {
        const colorList = ["#04B14B", "#C8242C", "#5C9AD2", "#A7CE39", "#9A268E"];

        return (
            <div className="facility-select-list-item" key={index}>
                <div className="facility-select-list-item-bullet"
                     style={{borderLeft: `20px solid ${colorList[index]}`}}>

                </div>
                <p className="facility-select-list-item-text" onClick={() => {
                    handleListItemClick(location)
                }}>{location.name}</p>
            </div>
        );
    });

    return (
        <div className="wizard-content-container">
            <div className="wizard-main-content-container">
                <div className="wizard-left-container">
                    <div className="wizard-icon-container">
                        <img src={facilityIcon} alt="Aurum Logo" className="wizard-icon"/>
                    </div>
                </div>
                <div className="wizard-right-container">
                    <div className="facility-select-list-container">
                        <h2>Please Choose a Location</h2>
                        {locations}
                        {locations.length === 0 && <p className="facility-select-message">No Recent Locations</p>}
                        <StandardButton text="Choose Another Location" onClick={handleChooseAnotherLocationClick}/>
                    </div>
                </div>
            </div>

            <div className="wizard-breadcrumb-container">
                <BreadCrumbTrail context={context} navigateTo={navigateTo}/>
            </div>

            <ReactModal isOpen={showModal} className="modal-content" overlayClassName="modal-overlay">
                <div className="facility-select-modal-content">
                    <p className="modal-title">Problem In Progress</p>
                    <p className="facility-select-modal-text">
                        You have a problem in progress.
                        If you start a new problem all progress on the current problem will be lost.
                    </p>
                    <div className="modal-button-container">
                        <StandardButton text="Start New Problem" onClick={handleNewProblemClick}/>
                        <StandardButton text="Cancel" onClick={() => {
                            setShowModal(false)
                        }}/>
                    </div>
                </div>
            </ReactModal>
        </div>
    );
}
